import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader, DataTable, Pill } from "../components/Table";
import { CheckCircle, XCircle, Barcode, ArrowRight } from "@phosphor-icons/react";

const TESTS = [
  ["gps_test", "GPS Signal"], ["gsm_test", "GSM Network"], ["ignition_test", "Ignition"],
  ["power_test", "Main Power"], ["battery_test", "Internal Battery"],
  ["panic1_test", "Panic Button 1"], ["panic2_test", "Panic Button 2"], ["packet_test", "Packet Communication"],
];

export default function Installations() {
  const [installs, setInstalls] = useState([]);
  const [wizard, setWizard] = useState(false);
  const [imei, setImei] = useState("");
  const [validated, setValidated] = useState(null);
  const [tests, setTests] = useState({});

  const load = () => api.get("/installations").then((r) => setInstalls(r.data.installations));
  useEffect(() => { load(); }, []);

  const validate = async () => {
    try {
      const r = await api.post(`/installations/validate-imei/${imei}`);
      setValidated(r.data);
      toast.success("IMEI validated");
    } catch (e) { setValidated(null); toast.error(e.response?.data?.detail || "IMEI not found"); }
  };

  const complete = async () => {
    if (!validated) return;
    try {
      await api.post("/installations", { vehicle_number: validated.device.imei.slice(-6), imei, ...tests });
      toast.success("Installation submitted for government activation");
      setWizard(false); setImei(""); setValidated(null); setTests({});
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const columns = [
    { key: "vehicle_number", header: "Vehicle", render: (r) => <span className="font-semibold">{r.vehicle_number}</span> },
    { key: "imei", header: "IMEI", render: (r) => <span className="font-mono text-xs">{r.imei}</span> },
    { key: "installer", header: "Installer" },
    { key: "status", header: "Status", render: (r) => <Pill color={r.status.includes("ACTIVATED") ? "#34C759" : "#FF9500"}>{r.status}</Pill> },
    { key: "installed_at", header: "Date", render: (r) => <span className="text-xs text-muted-foreground">{new Date(r.installed_at).toLocaleDateString()}</span> },
  ];

  return (
    <div className="p-5 space-y-4">
      <PageHeader title="Installations" subtitle="Server-verified AIS-140 installation flow"
        actions={<button data-testid="new-installation" onClick={() => setWizard(!wizard)} className="bg-primary text-white font-semibold text-sm px-4 py-2 rounded-sm">
          {wizard ? "Cancel" : "+ New Installation"}
        </button>} />

      {wizard && (
        <div className="bg-card border border-border rounded-sm p-5 space-y-4" data-testid="installation-wizard">
          <div className="flex gap-2 items-end">
            <div className="flex-1 max-w-md">
              <label className="label-caps flex items-center gap-1"><Barcode size={13} /> Scan / Enter IMEI</label>
              <input data-testid="install-imei" value={imei} onChange={(e) => setImei(e.target.value)} placeholder="869630058126156"
                className="mt-1 w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono" />
            </div>
            <button data-testid="validate-imei" onClick={validate} className="bg-secondary text-white font-semibold text-sm px-4 py-2 rounded-sm">Validate</button>
          </div>

          {validated && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-background rounded-sm border border-border">
                <div><div className="label-caps">Model</div><div className="text-sm font-semibold">{validated.device.model}</div></div>
                <div><div className="label-caps">SIM</div><div className="text-sm">{validated.device.sim_number}</div></div>
                <div><div className="label-caps">TAC</div><div className="text-sm">{validated.device.tac_number}</div></div>
                <div><div className="label-caps">Packet Received</div><div>{validated.packet_received ? <CheckCircle size={18} weight="fill" color="#34C759" /> : <XCircle size={18} weight="fill" color="#FF3B30" />}</div></div>
              </div>

              <div>
                <div className="label-caps mb-2">Device Tests</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {TESTS.map(([key, label]) => (
                    <button key={key} data-testid={`test-${key}`} onClick={() => setTests((t) => ({ ...t, [key]: !t[key] }))}
                      className={`flex items-center gap-2 p-2 rounded-sm border text-sm ${tests[key] ? "border-[#34C759] bg-[#34C759]/10" : "border-border hover:border-primary"}`}>
                      {tests[key] ? <CheckCircle size={16} weight="fill" color="#34C759" /> : <XCircle size={16} color="#8E8E93" />}
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <button data-testid="complete-installation" onClick={complete} disabled={!validated.packet_received}
                className="flex items-center gap-1.5 bg-primary text-white font-semibold text-sm px-4 py-2 rounded-sm disabled:opacity-50">
                Complete & Submit to Government <ArrowRight size={15} weight="bold" />
              </button>
              {!validated.packet_received && <p className="text-xs text-destructive">Cannot complete: server has not received a valid packet from this IMEI.</p>}
            </>
          )}
        </div>
      )}

      <DataTable testid="installations-table" columns={columns} rows={installs} />
    </div>
  );
}
